import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, ShieldCheck } from "lucide-react";

const ROLE_LABEL = { tenant_owner: "Owner", operator: "Operator", accountant: "Accountant" };
const PERMS = {
  tenant_owner: "Full access to all modules",
  operator: "Add collections · Print receipts · View farmers",
  accountant: "Payments · Reports · Export · View collections",
};
const EMPTY = { name: "", email: "", password: "", role: "operator", mobile: "" };

export default function Users() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const { data, isLoading } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });

  const create = useMutation({
    mutationFn: async () => (await api.post("/users", form)).data,
    onSuccess: () => { toast.success("User created"); setOpen(false); setForm(EMPTY); qc.invalidateQueries({ queryKey: ["users"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const toggle = useMutation({
    mutationFn: async (id) => (await api.post(`/users/${id}/toggle`)).data,
    onSuccess: () => { toast.success("User status updated"); qc.invalidateQueries({ queryKey: ["users"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  return (
    <div className="space-y-6" data-testid="users-page">
      <PageHeader
        title={t("users.title")}
        subtitle={t("users.subtitle")}
        breadcrumb={[t("crumb.center"), t("nav.users")]}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-user-btn"><Plus className="mr-1.5 h-4 w-4" /> {t("users.add")}</Button></DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle className="font-heading">{t("users.create")}</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-1.5"><Label>{t("common.name")} *</Label><Input value={form.name} onChange={set("name")} data-testid="user-name-input" /></div>
                <div className="space-y-1.5"><Label>{t("common.email")} *</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="user-email-input" /></div>
                <div className="space-y-1.5"><Label>{t("users.password")} *</Label><Input type="text" value={form.password} onChange={set("password")} data-testid="user-password-input" /></div>
                <div className="space-y-1.5"><Label>{t("common.mobile")}</Label><Input value={form.mobile} onChange={set("mobile")} data-testid="user-mobile-input" /></div>
                <div className="space-y-1.5">
                  <Label>{t("common.role")}</Label>
                  <Select value={form.role} onValueChange={set("role")}>
                    <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="operator">{t("role.operator")}</SelectItem><SelectItem value="accountant">{t("role.accountant")}</SelectItem><SelectItem value="tenant_owner">{t("role.tenant_owner")}</SelectItem></SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">{t(`perm.${form.role}`)}</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
                <Button onClick={() => create.mutate()} disabled={!form.name || !form.email || !form.password || create.isPending} data-testid="submit-user-btn">{create.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("common.create")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>{t("common.user")}</TableHead><TableHead>{t("common.role")}</TableHead><TableHead>{t("common.permissions")}</TableHead><TableHead>{t("common.status")}</TableHead><TableHead className="text-right">{t("common.active")}</TableHead></TableRow></TableHeader>
            <TableBody>
              {isLoading
                ? Array.from({ length: 4 }).map((_, i) => <TableRow key={i}><TableCell colSpan={5}><Skeleton className="h-8" /></TableCell></TableRow>)
                : (data || []).map((u) => (
                    <TableRow key={u.id} className="hover:bg-muted/50" data-testid={`user-row-${u.email}`}>
                      <TableCell><p className="font-medium">{u.name}</p><p className="text-xs text-muted-foreground">{u.email}</p></TableCell>
                      <TableCell><Badge variant={u.role === "tenant_owner" ? "default" : "outline"} className="gap-1"><ShieldCheck className="h-3 w-3" /> {t(`role.${u.role}`)}</Badge></TableCell>
                      <TableCell className="max-w-xs text-xs text-muted-foreground">{t(`perm.${u.role}`)}</TableCell>
                      <TableCell><Badge variant={u.status === "disabled" ? "secondary" : "default"}>{u.status || "active"}</Badge></TableCell>
                      <TableCell className="text-right">
                        <Switch checked={u.status !== "disabled"} disabled={u.role === "tenant_owner"} onCheckedChange={() => toggle.mutate(u.id)} data-testid={`toggle-user-${u.email}`} />
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
